<template>
	<footer
		class="w-100 ap-blue darken-2 pa-8 justify-center amelipro-footer"
		:class="footerDisplay"
		role="contentinfo"
		style="position: relative"
	>
		<div
			class="d-flex align-center amelipro-footer__content"
			:class="footerClasses"
			:style="'width:' + innerWidth"
		>
			<p
				v-if="!backOffice"
				class="mb-0 mr-4 ap-white--text text-caption font-weight-semibold footer-version"
				:class="versionDisplay"
			>
				{{ version }}
			</p>

			<div
				v-if="!backOffice"
				class="amelipro-footer__contact"
				:class="breakpointHelper.is('mdAndUp') ? 'mt-1 mr-4' : ''"
			>
				<a
					class="ap-white--text text-decoration-none amelipro-footer__contact__link"
					:class="breakpointHelper.is('mdAndUp') ? '' : 'align-center'"
					href="tel:3608"
				>
					<span class="text-uppercase text-button d-sr-only">
						{{ localesValue.contactLabel }}
					</span>

					<img
						alt="au 3608 : Service gratuit + prix appel"
						:src="require('../../assets/img/cartouche_nouveauTEL.svg')"
					>
				</a>
			</div>

			<ul
				v-if="!backOffice"
				class="mt-2 mt-sm-0 list-style-none d-flex flex-wrap align-center amelipro-footer__list"
				:class="breakpointHelper.is('mdAndUp') ? 'justify-end' : 'justify-center'"
			>
				<li
					v-if="!noSiteMap"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="siteMapHref"
						text
						:to="siteMapTo"
						@click="siteMapEvent"
					>
						{{ localesValue.siteMapLabel }}
					</AmeliproBtn>
				</li>

				<li
					v-if="!noAbout"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="aboutHref"
						text
						:to="aboutTo"
						@click="aboutEvent"
					>
						{{ localesValue.aboutLabel }}
					</AmeliproBtn>
				</li>

				<li
					v-if="!noConfiguration"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="configurationHref"
						text
						:to="configurationTo"
						@click="configEvent"
					>
						{{ localesValue.configurationLabel }}
					</AmeliproBtn>
				</li>

				<li
					v-if="!noLegalNotice"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="legalNoticeHref"
						text
						:to="legalNoticeTo"
						@click="legalNoticeEvent"
					>
						{{ localesValue.legalNoticeLabel }}
					</AmeliproBtn>
				</li>

				<li
					v-if="!noCgu"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="cguHref"
						text
						:to="cguTo"
						@click="cguEvent"
					>
						{{ localesValue.cguLabel }}
					</AmeliproBtn>
				</li>

				<li
					v-if="!noA11y"
					class="pa-2 footer-list-item"
				>
					<AmeliproBtn
						v-if="!noLinkA11y"
						class="text-none"
						color="ap-white"
						hover-color="ap-white"
						hover-underline
						:href="a11yHref"
						text
						:to="a11yTo"
						@click="a11yEvent"
					>
						{{ a11yComplianceLabel }}
					</AmeliproBtn>

					<p
						v-else
						class="mb-0 ap-white--text a11y-text"
					>
						{{ a11yComplianceLabel }}
					</p>
				</li>
			</ul>

			<p
				v-if="backOffice"
				class="mb-0 ap-white--text text-center font-weight-semibold amelipro-footer__backoffice-text"
			>
				CNAM - {{ backOfficeText }} - Version {{ version }}
			</p>
		</div>
		<div
			v-if="!backOffice"
			class="flex-column align-center footer-version--mobile"
			:class="versionDisplayMobile"
		>
			<p class="mt-2 mb-0 ap-white--text text-caption font-weight-semibold">
				{{ version }}
			</p>
		</div>
	</footer>
</template>

<script setup lang="ts">
	/* eslint-disable arrow-body-style */
	import { A11Y_COMPLIANCE_ENUM_VALUES, A11yComplianceEnum } from './A11yComplianceEnum';
	import { PropType, computed } from 'vue';
	import AmeliproBtn from '../AmeliproBtn/AmeliproBtn.vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { RawLocation } from 'vue-router';
	import { locales } from './locales';
	import { propValidator } from '../../helpers/propValidator';

	const props = defineProps({
		a11yCompliance: {
			default: A11yComplianceEnum.NON_COMPLIANT,
			type: String,
			validator: (value: string) => propValidator('a11yCompliance', A11Y_COMPLIANCE_ENUM_VALUES, value),
		},
		a11yHref: {
			type: String,
			default: undefined,
		},
		a11yTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		aboutHref: {
			type: String,
			default: undefined,
		},
		aboutTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		backOffice: {
			type: Boolean,
			default: false,
		},
		backOfficeText: {
			type: String,
			default: undefined,
		},
		cguHref: {
			type: String,
			default: undefined,
		},
		cguTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		configurationHref: {
			type: String,
			default: undefined,
		},
		configurationTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		legalNoticeHref: {
			type: String,
			default: undefined,
		},
		legalNoticeTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		noA11y: {
			type: Boolean,
			default: false,
		},
		noAbout: {
			type: Boolean,
			default: false,
		},
		noCgu: {
			type: Boolean,
			default: false,
		},
		noConfiguration: {
			type: Boolean,
			default: false,
		},
		noLegalNotice: {
			type: Boolean,
			default: false,
		},
		noLinkA11y: {
			type: Boolean,
			default: false,
		},
		noSiteMap: {
			type: Boolean,
			default: false,
		},
		siteMapHref: {
			type: String,
			default: undefined,
		},
		siteMapTo: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		version: {
			type: String,
			default: 'X.X.X',
		},
	});

	const breakpointHelper = new BreakpointHelper('smAndUp');
	const localesValue = locales;

	const footerDisplay = computed<string>(() => {
		return breakpointHelper.width < 1170 ? 'd-block' : 'd-flex';
	});

	const footerClasses = computed<string>(() => {
		if (props.backOffice) {
			return 'justify-center';
		}

		if (breakpointHelper.is('mdAndUp')) {
			return 'flex-row justify-space-between';
		}

		return 'flex-column';
	});

	const versionDisplay = computed<string>(() => {
		return breakpointHelper.is('mdAndUp') ? '' : 'd-none';
	});

	const versionDisplayMobile = computed<string>(() => {
		return breakpointHelper.is('mdAndUp') ? 'd-none' : 'd-flex';
	});

	const innerWidth = computed<string>(() => {
		if (breakpointHelper.width >= 1240) {
			return '1144px';
		}

		return breakpointHelper.width >= 1072 ? '980px' : '100%';
	});

	const a11yComplianceLabel = computed<string | null>(() => {
		const complianceLabel = localesValue[props.a11yCompliance as A11yComplianceEnum];
		if (!complianceLabel) {
			return null;
		}
		return locales.a11yLabel(complianceLabel);
	});

	const emit = defineEmits(['site-map-event', 'about-event', 'config-event', 'legal-notice-event', 'cgu-event', 'a11y-event']);
	const siteMapEvent = (): void => {
		emit('site-map-event');
	};

	const aboutEvent = (): void => {
		emit('about-event');
	};

	const configEvent = (): void => {
		emit('config-event');
	};

	const legalNoticeEvent = (): void => {
		emit('legal-notice-event');
	};

	const cguEvent = (): void => {
		emit('cgu-event');
	};

	const a11yEvent = (): void => {
		emit('a11y-event');
	};
</script>

<style lang="scss" scoped>
:deep(.v-btn):focus {
	outline-color: $ap-white;
}

.a11y-text {
	display: inline-block;
	line-height: 1.4 !important;
}
</style>
